import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { AiCameraIcon, QuestionIcon } from 'components/CommonComponents/JSXIcons'
import { Button, Chip } from 'components/ui'
import { SubtaskPreviewCard } from 'components/TasksV2/TaskCreation/components/SubtaskPreviewCard'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { isRtlSelector } from 'redux/selectors/general'
import keys from 'constants/keys'
import { usePromptGenerator } from '../../PromptGeneratorContext'
import { AIStepType, useAILoadingContext } from '../../AIFormStepContext'
import { useAiCreationText } from '../../hooks/useAiCreationText'
import AIPromptStep from '../AIPromptStep'
import PromptGeneratorStep from '../PromptGeneratorStep'
import { PromptGeneratorInput } from '../PromptGeneratorStep/PromptGeneratorInput'
import { AIResetDialog } from '../AIResetDialog'
import { SlideInPanel, useStepDirection } from 'components/TasksV2/UI/SlideInPanel'
import styles from './styles.module.css'

export function AIPromptWorkspace({
  onReset,
  onPromptReadyChange,
}: {
  onReset: () => void
  onPromptReadyChange: (ready: boolean) => void
}) {
  const { stepStack, formData, goToPreviousStep, isMediaUploading } = useFormStepContext()
  const { selectedSubtaskType } = useAILoadingContext()
  const rtl = useSelector(isRtlSelector)
  const t = useAiCreationText()
  const { clearHistory } = usePromptGenerator()
  const [confirm, setConfirm] = useState(false)
  const helper = stepStack[stepStack.length - 1] === AIStepType.AI_PROMPT_GENERATOR
  const { direction } = useStepDirection(stepStack.length)
  const question = selectedSubtaskType === 'question'
  const Icon = question ? QuestionIcon : AiCameraIcon
  const title = question ? t.question : t.photo
  const prompt = (formData as any).AI_PROMPT
  const field = {
    id: 'ai-audit-subtask',
    title,
    type: question ? keys.SUBTASK_TYPES.OPEN_QUESTION : keys.SUBTASK_TYPES.IMAGE_SUBTASK,
    is_required: true,
    content: { images: prompt?.reference_images || [] },
  }

  return (
    <div className={styles.workspace} dir={rtl ? 'rtl' : 'ltr'}>
      <section className={styles.list}>
        <header>{t.prompt}</header>
        <div className={styles.cards}>
          <SubtaskPreviewCard
            field={field as any}
            index={0}
            isAi={!question}
            isSelected
            hasError={false}
            busy={isMediaUploading}
            rtl={rtl}
            Icon={Icon}
            sortable={false}
            onSelect={() => {}}
            onRemove={() => setConfirm(true)}
          />
        </div>
      </section>
      <section className={styles.editor}>
        <header>
          {helper && (
            <Button type="button" variant="icon" aria-label={t.back} onClick={goToPreviousStep}>
              {rtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          )}
          <Icon width="42px" height="42px" />
          <div>
            <strong>{title}</strong>
            <small>{helper ? t.helper : t.prompt}</small>
          </div>
          <Chip
            label={<>{t.required}</>}
            size="sm"
            bg="#fff0db"
            color="#8a5415"
            style={{ marginInlineStart: 'auto' }}
          />
        </header>
        <div className={`${styles.body} ${helper ? styles.chat : ''}`}>
          <SlideInPanel key={helper ? 'helper' : 'prompt'} direction={direction} grow>
            {helper ? <PromptGeneratorStep /> : <AIPromptStep showContinue onReadyChange={onPromptReadyChange} />}
          </SlideInPanel>
        </div>
        {helper && (
          <footer className={styles.composer}>
            <PromptGeneratorInput />
          </footer>
        )}
      </section>
      {confirm && (
        <AIResetDialog
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            clearHistory()
            onReset()
          }}
        />
      )}
    </div>
  )
}
